import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbCalendarTime } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { formatDay, formatTime, isSameDay } from "./calendarRenderUtils";
import { type FreebusyPayload, parseFreebusyPayload } from "./freebusySchema";

interface FreebusyRendererProps {
  block: StreamingBlock;
}

const intervalLabel = (start: string, end: string): string => {
  const day = formatDay(start);
  if (isSameDay(start, end)) return `${day} · ${formatTime(start)}–${formatTime(end)}`;
  return `${day} ${formatTime(start)} – ${formatDay(end)} ${formatTime(end)}`;
};

export const FreebusyRenderer = (props: FreebusyRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<FreebusyPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseFreebusyPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("freebusy_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
              <TbCalendarTime class="size-4 shrink-0 text-brand" />
              <span class="text-sm font-medium text-primary-text">{rt.t("freebusy_card.title")}</span>
              <span class="ml-auto text-xs text-muted-text">{intervalLabel(data().timeMin, data().timeMax)}</span>
            </div>

            <Show
              when={data().busy.length > 0}
              fallback={<div class="p-3 text-sm text-muted-text">{rt.t("freebusy_card.free")}</div>}
            >
              <ul class="divide-y divide-primary-border">
                <For each={data().busy}>
                  {(interval) => (
                    <li class="flex items-center gap-2 px-3 py-2">
                      <span class="size-2 shrink-0 rounded-full bg-red-500" aria-hidden="true" />
                      <span class="text-sm text-primary-text">{rt.t("freebusy_card.busy")}</span>
                      <span class="ml-auto text-xs text-muted-text">{intervalLabel(interval.start, interval.end)}</span>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
